import { computed, type ComputedRef, type Ref } from 'vue';
import type { MenuItem } from '@boqnet/ui';

import router from '@/router';
import i18n from '@/plugins/vue-i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useFeatureFlagsStore } from '@/stores/feature-flags.store';
import { selectedOrgId } from '@/common/utils/role.utils';
import { isInternalViewsEnabled } from '@/utils/flags';

import {
  ArrowRightEndOnRectangleIcon,
  BuildingOffice2Icon,
  CogIcon,
  EnvelopeIcon,
  IdentificationIcon,
  TableCellsIcon,
  UserIcon,
  UsersIcon,
} from '@heroicons/vue/24/outline';

/**
 * Returns the `items` for the account/profile dropdown shared by NavMenu and MobileNavigation.
 *
 * `showSettingItems` comes from the caller (DefaultLayout/NavigationOnlyLayout, both allowlisted
 * for the legacy `roles.store` predicate) rather than being read here, so this composable stays
 * off the `permissions/no-legacy-role-predicates` allowlist per that rule's own instruction.
 */
export function useAccountMenuItems(showSettingItems: Ref<boolean> | ComputedRef<boolean>) {
  const { t } = i18n.global;
  const authStore = useAuthStore();
  const featureFlagsStore = useFeatureFlagsStore();

  const items = computed<MenuItem[]>(() => [
    {
      id: 'account-details',
      label: t('navigation.accountDetails'),
      icon: UserIcon,
      command: () =>
        router.push({
          name: 'account-details',
          hash: '#account',
          params: { organisationId: selectedOrgId.value },
        }),
      visible: false,
    },
    {
      id: 'notification-settings',
      label: t('navigation.notificationSettings'),
      icon: EnvelopeIcon,
      command: () =>
        router.push({
          name: 'account-details',
          hash: '#notifications',
          params: { organisationId: selectedOrgId.value },
        }),
      visible: false,
    },
    {
      id: 'user-permissions',
      label: t('navigation.userPermissions'),
      icon: UsersIcon,
      command: () =>
        router.push({
          name: 'organisation-details',
          hash: '#users',
          params: { organisationId: selectedOrgId.value },
        }),
      visible: showSettingItems.value,
    },
    {
      id: 'organisation-details',
      label: t('navigation.organisationDetails'),
      icon: BuildingOffice2Icon,
      command: () =>
        router.push({
          name: 'organisation-details',
          hash: '#organisation',
          params: { organisationId: selectedOrgId.value },
        }),
      visible: showSettingItems.value,
    },
    {
      id: 'organisation-settings',
      label: t('navigation.organisationSettings'),
      icon: CogIcon,
      command: () =>
        router.push({
          name: 'organisation-details',
          hash: '#settings',
          params: { organisationId: selectedOrgId.value },
        }),
      visible: showSettingItems.value,
    },
    {
      id: 'permissions-matrix',
      label: t('navigation.permissionsMatrix'),
      icon: TableCellsIcon,
      command: () => router.push({ name: 'permissions-matrix' }),
      // Context: a developer reference, so every role sees it wherever the route exists.
      visible: isInternalViewsEnabled,
    },
    {
      id: 'role-capabilities',
      label: t('navigation.roleCapabilities'),
      icon: IdentificationIcon,
      command: () =>
        router.push({
          name: 'role-capabilities',
          params: { organisationId: selectedOrgId.value },
        }),
      // Context: every signed-in user, so not tied to `showSettingItems`. An unresolved flag reads
      // as off, so the entry only appears once LaunchDarkly says it's on.
      visible: featureFlagsStore.roleCapabilityOverviewEnabled,
    },
    {
      id: 'account-menu-separator',
      type: 'separator',
      label: 'separator',
      visible:
        showSettingItems.value ||
        isInternalViewsEnabled ||
        featureFlagsStore.roleCapabilityOverviewEnabled,
    },
    {
      id: 'logout',
      label: t('general.logout'),
      icon: ArrowRightEndOnRectangleIcon,
      command: () => authStore.logout(),
    },
  ]);

  return { items };
}
